/**
 * Shopify Checkout Utilities
 * 
 * Centralized logic for generating Shopify cart URLs and handling checkout flows.
 * Used by both CarritoStep and QuoteView for consistent checkout behavior.
 */

// Updated Shopify variant mapping with real bundle variant IDs
export const SHOPIFY_BUNDLE_VARIANTS: Record<string, Record<string, string>> = {
  // YH-680B + YN-03 (Nasal)
  'yh-680b-apap-yn-03': { 
    S: '46624737427668', // Pequeña
    M: '46624736870612', // Mediana
    L: '46624737460436'  // Grande
  },
  // YH-680B + YF-02 (Oronasal)
  'yh-680b-apap-yf-02': { 
    S: '46690770485460', // Pequeña
    M: '46690770518228', // Mediana
    L: '46690770550996'  // Grande
  },
  // AirSense 10 + N20 (Nasal)
  'airsense-10-apap-n20': { 
    S: '46690770583764', // Pequeña
    M: '46690770616532', // Mediana
    L: '46690770649300'  // Grande
  },
  // AirSense 10 + F20 (Oronasal)
  'airsense-10-apap-f20': { 
    S: '46690770682068', // Pequeña
    M: '46690770714836', // Mediana
    L: '46690770747604'  // Grande
  },
  // AirSense 10 + YN-03 (Nasal)
  'airsense-10-apap-yn-03': { 
    S: '46690770780372', // Pequeña
    M: '46690770813140', // Mediana
    L: '46690770845908'  // Grande
  },
  // AirSense 10 + YF-02 (Oronasal)
  'airsense-10-apap-yf-02': { 
    S: '46690770878676', // Pequeña
    M: '46690770911444', // Mediana
    L: '46690770944212'  // Grande
  },
  // YH-680B + F20 (Oronasal)
  'yh-680b-apap-f20': { 
    S: '46770273583316', // Pequeña
    M: '46770273616084', // Mediana
    L: '46770273648852'  // Grande
  },
  // YH-680B + N20 (Nasal)
  'yh-680b-apap-n20': { 
    S: '46770278203604', // Pequeña
    M: '46770278236372', // Mediana
    L: '46770278269140'  // Grande
  }
};

// BUNO_PLUS_WITH_PRICE - Backwards-compatible variant ID handling
export const BUNO_PLUS_VARIANT_ID_OLD = '46770332205268'; // Used until Feb 16, 2026
export const BUNO_PLUS_VARIANT_ID_NEW = '47561280979156'; // Current
export const VARIANT_ID_CUTOFF_DATE = new Date('2026-02-16T00:00:00Z');

/**
 * Gets the correct Buno+ variant ID based on when the quote was created
 * @param createdAt - Quote creation timestamp (optional)
 * @returns The appropriate Buno+ variant ID
 */
export const getBunoPlusVariantId = (createdAt?: Date | string): string => {
  if (!createdAt) {
    return BUNO_PLUS_VARIANT_ID_NEW;
  }

  const quoteDate = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;

  if (quoteDate < VARIANT_ID_CUTOFF_DATE) {
    console.log(`📅 Using old Buno+ variant for quote from ${quoteDate.toISOString()}`);
    return BUNO_PLUS_VARIANT_ID_OLD;
  }

  return BUNO_PLUS_VARIANT_ID_NEW;
};

export const SHOPIFY_STORE_URL = 'https://m650id-ee.myshopify.com';

// Bundle info type for cart generation
export interface BundleInfoForCart {
  id: string;
  isShopifyBundle: boolean;
  variants: Array<{ id: string; title: string; price: { amount: string } }>;
  bundleMapping?: {
    shopifyHandle: string;
    variants: {
      S: string;
      M: string;
      L: string;
    };
  };
}

// Helper function to extract numeric variant ID from GID format
export const extractVariantId = (variantId: string): string => {
  if (variantId.includes('gid://shopify/ProductVariant/')) {
    return variantId.replace('gid://shopify/ProductVariant/', '');
  }
  return variantId;
};

/**
 * Generate Shopify cart URL with selected products
 * @param bundleInfo Bundle information with variants and mapping
 * @param maskSize Selected mask size (S/M/L)
 * @param includeBuno Whether to include buno+ program
 * @param quoteData Optional quote data for backwards-compatible variant ID resolution
 * @returns Shopify cart URL or null if generation fails
 */
export const generateShopifyCartUrl = (
  bundleInfo: BundleInfoForCart,
  maskSize: string | undefined,
  includeBuno: boolean,
  quoteData?: { bunoPlusVariantId?: string; createdAt?: Date | string }
): string | null => {
  try {
    const size = maskSize || 'M'; // Default to Medium if no size selected
    let productVariantId: string | null = null;

    // First priority: Use bundle mapping if available
    if (bundleInfo.bundleMapping?.variants) {
      productVariantId = bundleInfo.bundleMapping.variants[size as keyof typeof bundleInfo.bundleMapping.variants];
      if (productVariantId) {
        console.log(`Using bundle mapping variant: ${productVariantId} for size ${size} from mapping: ${bundleInfo.bundleMapping.shopifyHandle}`);
      }
    }
    
    // Second priority: If we have a real Shopify bundle, use its variants  
    if (!productVariantId && bundleInfo.isShopifyBundle && bundleInfo.variants.length > 0) {
      // Map size to Spanish variant names
      const sizeMapping = {
        'S': ['pequeña', 'small'],
        'M': ['mediana', 'medium'], 
        'L': ['grande', 'large']
      };
      
      const sizeVariant = bundleInfo.variants.find((variant) => {
        const variantTitle = variant.title.toLowerCase();
        const sizeOptions = sizeMapping[size as keyof typeof sizeMapping] || [];
        return sizeOptions.some(sizeOption => variantTitle.includes(sizeOption));
      });
      
      // Extract numeric variant ID (remove GID prefix if present)
      productVariantId = sizeVariant 
        ? extractVariantId(sizeVariant.id)
        : extractVariantId(bundleInfo.variants[0].id);
      
      console.log(`Using Shopify bundle variant: ${productVariantId} for size ${size} from bundle: ${bundleInfo.id}`);
    }
    
    // Third priority: Fallback to hardcoded mapping (legacy)
    if (!productVariantId) {
      const variantMapping = SHOPIFY_BUNDLE_VARIANTS[bundleInfo.id];
      
      if (!variantMapping) {
        console.warn(`No variant mapping found for bundle: ${bundleInfo.id}`);
        return null;
      }
      
      if (!variantMapping[size]) {
        console.warn(`No variant found for bundle: ${bundleInfo.id}, size: ${size}. Available sizes:`, Object.keys(variantMapping));
        // Fallback to medium size if selected size not available
        productVariantId = variantMapping['M'] || Object.values(variantMapping)[0];
      } else {
        productVariantId = variantMapping[size];
      }
      
      console.log(`Using legacy variant mapping: ${productVariantId} for bundle ${bundleInfo.id}, size ${size}`);
    }

    if (!productVariantId) {
      console.error('Could not determine product variant ID');
      return null;
    }
    
    // Ensure we have numeric variant IDs for cart URL
    const numericVariantId = extractVariantId(productVariantId);
    const cartItems = [];

    // Add buno+ first if included (so it appears at top of Shopify cart)
    if (includeBuno) {
      const bunoPlusId = quoteData?.bunoPlusVariantId ||
                         getBunoPlusVariantId(quoteData?.createdAt);

      console.log(`💳 Using Buno+ variant: ${bunoPlusId}`, {
        stored: !!quoteData?.bunoPlusVariantId,
        createdAt: quoteData?.createdAt,
        source: quoteData?.bunoPlusVariantId ? 'stored' : 'timestamp'
      });

      cartItems.push(`${bunoPlusId}:1`);
    }

    // Then add the bundle
    cartItems.push(`${numericVariantId}:1`);
    
    const cartUrl = `${SHOPIFY_STORE_URL}/cart/${cartItems.join(',')}`;
    console.log(`Generated Shopify cart URL: ${cartUrl}`);
    return cartUrl;
  } catch (error) {
    console.error('Error generating Shopify cart URL:', error);
    return null;
  }
};

/**
 * Handle Shopify checkout flow
 * @param bundleInfo Bundle information
 * @param maskSize Selected mask size
 * @param includeBuno Whether to include buno+ program
 * @param quoteData Optional quote data for backwards-compatible variant ID resolution
 * @param onError Callback for handling errors
 */
export const handleShopifyCheckout = (
  bundleInfo: BundleInfoForCart,
  maskSize: string | undefined,
  includeBuno: boolean,
  quoteData?: { bunoPlusVariantId?: string; createdAt?: Date | string },
  onError?: (error: string) => void
): void => {
  const shopifyUrl = generateShopifyCartUrl(bundleInfo, maskSize, includeBuno, quoteData);
  
  if (shopifyUrl) {
    // Log bundle information for debugging
    console.log('Bundle Info:', bundleInfo);
    console.log('Using Shopify Bundle:', bundleInfo.isShopifyBundle);
    
    // Open Shopify cart in current tab
    window.location.href = shopifyUrl;
  } else {
    const errorMessage = 'Failed to generate Shopify checkout URL';
    console.error(errorMessage);
    if (onError) {
      onError(errorMessage);
    }
  }
};